/* ============================================
   QUOTE WORKSTATION — Customer
   Asset: workstation-customer.js
   Customer form, autocomplete, address toggles, status badge
   ============================================ */
(function() {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;
  var state = Q.state;
  var dom = Q.dom;
  var wrap = Q.wrap;
  var API = Q.API;
  var showToast = Q.showToast;
  var syncCustomSelect = Q.syncCustomSelect;
  var escHtml = Q.escHtml;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — Chip-style multi-email on the
     customer card (Sold To / Ship To / Bill To). Each field accepts comma-
     separated addresses as removable chips (first = primary; the worker's
     findOrCreateCustomer takes the first for Shopify, the full joined value is
     stored on wm.customer for Outlook thread-matching). The original inputs
     survive as hidden mirrors (id/data-field preserved) so the save collector +
     validation read the joined value unchanged; autocomplete + CFA detection are
     rebound to the chip's visible typing input below. */
  var _emailChipApis = {};
  (function installCustomerEmailChips() {
    if (!(window.RepPortal && window.RepPortal.installChipField)) return;
    ['email', 'shipEmail', 'billEmail', 'emailCc'].forEach(function(f) {
      var el = wrap.querySelector('[data-field="' + f + '"]');
      if (!el) return;
      /* CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) - the Sold To's To and CC boxes hold PEOPLE (a name with each
         address, pasted Outlook lines keep theirs, click a pill to name it); Ship To / Bill To stay address chips. */
      var people = f === 'email' || f === 'emailCc';
      var api = window.RepPortal.installChipField({
        replace: el,
        placeholder: f === 'emailCc' ? 'Anyone to copy' : f === 'email' ? 'Main contact first, then anyone else to email' : '',
        ariaLabel: f === 'email' ? 'To' : f === 'emailCc' ? 'CC' : 'Email addresses',
        named: people,
        noPrimary: f === 'emailCc',
        onChange: function() { state.dirty = true; },
      });
      if (api) _emailChipApis[f] = api;
    });
  })();
  /* CUSTOM_DEALPEOPLE_BY_CLAUDE - what the loaded record knew about each extra person that the pills do not show (a phone,
     a title) and the people with no address at all, so a save sends them back instead of dropping them. */
  var _peopleExtras = {};
  var _peopleNoEmail = [];
  function seedDealPeople(c, q) {
    var CT = window.RepPortal && window.RepPortal.Contacts;
    var toApi = _emailChipApis.email, ccApi = _emailChipApis.emailCc;
    if (!CT || !toApi || !ccApi) return;
    var people = CT.readContacts({
      name: q.customerName || c.name || '', email: q.customerEmail || c.email || '',
      contacts: Array.isArray(c.contacts) ? c.contacts : (Array.isArray(q.customerContacts) ? q.customerContacts : undefined),
    });
    _peopleExtras = {}; _peopleNoEmail = [];
    var to = [], cc = [];
    people.forEach(function(p, i) {
      if (i === 0) { if (p.email) to.push({ name: p.name, email: p.email }); return; }
      if (!p.email) { _peopleNoEmail.push({ name: p.name, title: p.title, phone: p.phone, role: p.role }); return; }
      _peopleExtras[p.email.toLowerCase()] = { phone: p.phone || '', title: p.title || '' };
      (p.role === 'to' ? to : cc).push({ name: p.name, email: p.email });
    });
    toApi.setPeople(to);
    ccApi.setPeople(cc);
  }

  function populateCustomer(q) {
    var c = q.customer || q;
    var fields = ['name', 'email', 'phone', 'company',
                   'street', 'street2', 'city', 'state', 'zip', 'attn',
                   'shipName', 'shipEmail', 'shipPhone', 'shipCompany',
                   'shipStreet', 'shipStreet2', 'shipCity', 'shipState', 'shipZip', 'shipAttn',
                   'billName', 'billEmail', 'billPhone', 'billCompany',
                   'billStreet', 'billStreet2', 'billCity', 'billState', 'billZip', 'billAttn'];
    fields.forEach(function(f) {
      var input = wrap.querySelector('[data-field="' + f + '"]');
      if (input) {
        var val = c[f] || q[f] || '';
        if (f === 'name') val = q.customerName || c.name || q.customer_name || val;
        if (f === 'email') val = q.customerEmail || c.email || q.customer_email || val;
        if (f === 'phone') val = q.customerPhone || c.phone || q.customer_phone || val;
        if (f === 'company') val = q.customerCompany || c.company || q.customer_company || val;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Strip legacy em-dash auto-placeholders
           (older saves filled blank fields with `—`). DO NOT strip a single
           hyphen `-` — reps type that intentionally as a "TBD" placeholder
           on fields like email when the customer hasn't given one yet, and
           expect it to persist across reloads. */
        if (val === '—') val = '';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        input.value = val;
      }
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — reseed the email chip fields
       from the (hidden-mirror) values just set above, splitting comma-joined
       addresses into chips. Without this the chips wouldn't render on load. */
    ['email', 'shipEmail', 'billEmail'].forEach(function(f) {
      if (!_emailChipApis[f]) return;
      var hid = wrap.querySelector('[data-field="' + f + '"]');
      _emailChipApis[f].setChips(String((hid && hid.value) || '').split(/[,;]/));
    });
    seedDealPeople(c, q); // CUSTOM_DEALPEOPLE_BY_CLAUDE - the To / CC people, with their names
    // Format phone
    var phoneInput = wrap.querySelector('[data-field="phone"]');
    if (phoneInput && phoneInput.value && window.RepPortal && window.RepPortal.formatPhone) {
      phoneInput.value = window.RepPortal.formatPhone(phoneInput.value);
    }
    // Restore Ship To / Bill To toggles — em-dash placeholder check only;
    // a user-typed hyphen counts as a real (deliberate) value.
    var notDash = function(v) { return v && v !== '—'; };
    var hasShip = c.hasShipTo || !!(notDash(c.shipName) || notDash(c.shipCompany) || notDash(c.shipStreet) || notDash(c.shipCity));
    var hasBill = c.hasBillTo || !!(notDash(c.billName) || notDash(c.billCompany) || notDash(c.billStreet) || notDash(c.billCity));
    var shipCb = wrap.querySelector('[data-addr-toggle="shipTo"]');
    var billCb = wrap.querySelector('[data-addr-toggle="billTo"]');
    if (hasShip && shipCb && !shipCb.checked) { shipCb.checked = true; shipCb.dispatchEvent(new Event('change')); }
    if (hasBill && billCb && !billCb.checked) { billCb.checked = true; billCb.dispatchEvent(new Event('change')); }
  }

  var _statusTimer = null;
  function setStatusBadge(status) {
    var badge = dom.barStatus;
    var s = (status || '').toLowerCase();
    if (_statusTimer) { clearTimeout(_statusTimer); _statusTimer = null; }

    // Only show badge for transient feedback states
    if (s === 'saved' || s === 'sent') {
      badge.textContent = s === 'saved' ? 'Saved' : 'Sent';
      badge.className = 'rq-ws-bar__status rq-ws-bar__status--' + s;
      badge.style.display = '';
      _statusTimer = setTimeout(function() { badge.style.display = 'none'; }, 3000);
    } else {
      // Hide for all other states (draft, reviewing, pending, etc.)
      badge.style.display = 'none';
    }
  }

  /* ── New Quote ── */
  if (dom.btnNew) {
    dom.btnNew.addEventListener('click', function() {
      if (state.dirty && state.items.length > 0) {
        if (!confirm('You have unsaved changes. Start a new quote?')) return;
      }
      resetToNew();
    });
  }

  /* ── Dashboard link ── */
  var btnDashboard = wrap.querySelector('.rq-ws-bar__btn--dashboard');
  if (btnDashboard) {
    btnDashboard.addEventListener('click', function() {
      if (state.dirty && state.items.length > 0) {
        if (!confirm('You have unsaved changes. Leave for the Dashboard?')) return;
      }
      window.location.href = '/pages/rep-quote-dashboard';
    });
  }

  function resetToNew() {
    if (state.locked) Q.unlockWorkstation();
    if (typeof Q.setLeadContext === 'function') Q.setLeadContext(null);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — drop replacement mode. Without this,
       clicking "+ New Quote" straight after working a replacement leaves the price columns
       greyed and Send disabled on a genuine new quote, with no obvious way out. */
    state.isReplacement = false;
    state.replacementParentRef = '';
    state.replacementReason = '';
    state.replacementCandidates = [];
    wrap.classList.remove('rq-ws--replacement');
    if (Q.renderReplacementPicker) Q.renderReplacementPicker();
    /* The top-bar "Replacement for X" chip is display state, so the class removal above
       cannot take it with it — same reason the picker needs its own call. */
    if (Q.renderReplacementFor) Q.renderReplacementFor();
    var _sendBtnR = wrap.querySelector('.rq-ws-send__send');
    if (_sendBtnR) { _sendBtnR.disabled = false; _sendBtnR.title = ''; }
    state.mode = 'new';
    state.ref = null;
    if (Q.fillDeliveryWindow) Q.fillDeliveryWindow(null); // CUSTOM_QUOTEDWINDOW_BY_CLAUDE - a new quote starts with no delivery window
    state.items = [];
    state.customFees = [];
    state.fees = [
      { title: 'Shipping & Freight', amount: 0, status: 'pending' },
      { title: 'Installation', amount: 0, status: 'none' } // CUSTOM_QWINSTALLNONE_BY_CLAUDE (Ryan 2026-10-11)
    ];
    state.cardFee = { status: 'pending', amount: 0 };
    state.tax = { mode: 'pending', rate: 0, amount: 0, includeFreight: false, includeInstallation: false };
    state.freight = {
      status: 'pending', requestedAt: null, requestedBy: null,
      vendors: [], blanketMarkup: false, blanketMarkupPct: 10,
      totalCost: 0, totalQuoted: 0, appliedAt: null, history: [], itemsSnapshot: []
    };
    state.installation = {
      status: 'none', scope: '', requestedAt: null, requestedBy: null,
      companies: [], totalCost: 0, totalQuoted: 0, appliedAt: null, history: []
    };
    state.leadSource = '';
    state.cfaAttribution = '';
    state.referral = { name: '', gpPercent: 0 }; // BLOCK A — prevent stale recipient leaking into next quote
    state.referralSource = { name: '' }; // BLOCK B — prevent stale referral source leaking
    state.dirty = false;
    state.pdfDownloaded = false;
    state.loadedGrandTotal = null;
    state.lastGrandTotal = 0;
    var refNameEl0 = wrap.querySelector('input[data-field="referralName"]');
    var refPctEl0  = wrap.querySelector('input[data-field="referralGpPercent"]');
    if (refNameEl0) refNameEl0.value = '';
    if (refPctEl0)  refPctEl0.value = '';
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — central reset hides the Shared Deal
    // recipient block for the fresh quote (state.leadSource already cleared above).
    if (Q.updateReferralBlockVisibility) Q.updateReferralBlockVisibility();
    else { var referralBlk0 = wrap.querySelector('[data-referral-block]'); if (referralBlk0) referralBlk0.classList.remove('rq-ws-referral--open'); }

    var lsSel = wrap.querySelector('select[data-field="leadSource"]'); if (lsSel) { lsSel.value = ''; syncCustomSelect(lsSel); }
    var cfaSel = wrap.querySelector('select[data-field="cfaAttribution"]'); if (cfaSel) { cfaSel.value = ''; syncCustomSelect(cfaSel); }

    if (dom.btnNew) dom.btnNew.classList.add('rq-ws-bar__btn--active');
    dom.btnLoad.classList.remove('rq-ws-bar__btn--active');
    dom.barRef.textContent = '';
    dom.barCustomer.textContent = '';
    dom.barRef.style.display = '';
    dom.barStatus.textContent = '';
    if (Q.sentEmails) { for (var k in Q.sentEmails) delete Q.sentEmails[k]; }
    dom.barStatus.style.background = '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Hide the completed-draft banner on
       new-quote reset. Shown again only when loadQuote pulls a quote whose
       Shopify draft.status === 'COMPLETED'. */
    var _frozenBanner0 = wrap.querySelector('.rq-ws-frozen-banner');
    if (_frozenBanner0) _frozenBanner0.style.display = 'none';
    /* CUSTOM_QWALERTS_BY_CLAUDE (Pass 2) - a new quote carries none of the last quote's alerts. */
    state.paidThroughShopify = false;
    state.freightHold = null;
    if (Q.clearQuoteAlerts) Q.clearQuoteAlerts();
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Clear customer
    wrap.querySelectorAll('.rq-ws-customer__grid input, .rq-ws-customer__grid select, .rq-ws-customer__grid textarea').forEach(function(el) {
      if (el.tagName === 'SELECT') el.selectedIndex = 0;
      else el.value = '';
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — also empty the email chip
       fields (clearing the hidden mirror above doesn't remove rendered chips). */
    ['email', 'shipEmail', 'billEmail'].forEach(function(f) { if (_emailChipApis[f]) _emailChipApis[f].setChips([]); });

    // Reset Ship To / Bill To
    wrap.querySelectorAll('[data-addr-toggle]').forEach(function(cb) {
      if (cb.checked) { cb.checked = false; cb.dispatchEvent(new Event('change')); }
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — also clear the autocomplete stash so a
       fresh quote doesn't lazy-apply a prior customer's alternate address. */
    _stashedShipTo = null;
    _stashedBillTo = null;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Expand customer
    var custBody = wrap.querySelector('.rq-ws-customer .rq-ws-section__body');
    var custIcon = wrap.querySelector('.rq-ws-customer .rq-ws-section__toggle-icon');
    if (custBody) { custBody.classList.add('rq-ws-section__body--open'); custBody.style.maxHeight = 'none'; custBody.style.overflow = 'visible'; }
    if (custIcon) custIcon.textContent = '−';

    // Clear fees
    wrap.querySelectorAll('.rq-ws-fees__input').forEach(function(i) { i.value = ''; i.style.display = 'none'; });
    wrap.querySelectorAll('.rq-ws-fees__status').forEach(function(s) {
      s.value = s.getAttribute('data-fee') === 'installation' ? 'none' : 'pending'; // CUSTOM_QWINSTALLNONE_BY_CLAUDE
      syncCustomSelect(s);
    });
    dom.feesCustomRows.innerHTML = '';
    // Reset tax UI
    var taxInclReset = wrap.querySelector('.rq-ws-fees__tax-includes');
    if (taxInclReset) taxInclReset.style.display = 'none';
    wrap.querySelectorAll('.rq-ws-fees__tax-check-input').forEach(function(cb) { cb.checked = false; });

    // Reset expiration
    var d30 = new Date();
    d30.setDate(d30.getDate() + 30);
    wrap.querySelector('[data-field="expiration"]').value = d30.toISOString().split('T')[0];

    // Hide success
    dom.successPanel.style.display = 'none';

    // Update URL
    if (window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    Q.renderItems();
    Q.recalcTotals();
    state.dirty = false;
    Q.updateSaveButton();
  }

  /* ── Zip Auto-Lookup (Sold To, Ship To, Bill To) ── */
  function bindZipLookup(zipField, cityField, stateField) {
    var zipEl = wrap.querySelector('[data-field="' + zipField + '"]');
    if (!zipEl) return;
    zipEl.addEventListener('blur', function() {
      var zip = this.value.trim();
      if (zip.length === 5 && /^\d{5}$/.test(zip)) {
        fetch('https://api.zippopotam.us/us/' + zip)
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(data) {
            if (data && data.places && data.places[0]) {
              var p = data.places[0];
              var cityInput = wrap.querySelector('[data-field="' + cityField + '"]');
              var stateInput = wrap.querySelector('[data-field="' + stateField + '"]');
              if (cityInput && !cityInput.value) cityInput.value = p['place name'] || '';
              if (stateInput && !stateInput.value) stateInput.value = p['state abbreviation'] || '';
            }
          })
          .catch(function() {});
      }
    });
  }
  bindZipLookup('zip', 'city', 'state');
  bindZipLookup('shipZip', 'shipCity', 'shipState');
  bindZipLookup('billZip', 'billCity', 'billState');

  /* ── Phone Formatting ── */
  ['phone', 'shipPhone', 'billPhone'].forEach(function(f) {
    var input = wrap.querySelector('[data-field="' + f + '"]');
    if (input && window.RepPortal && window.RepPortal.bindPhoneInput) {
      window.RepPortal.bindPhoneInput(input);
    }
  });

  /* ── Organization italic for residence values ── */
  var orgInput = wrap.querySelector('[data-field="company"]');
  function checkOrgItalic() {
    var v = orgInput.value.trim().toLowerCase();
    orgInput.style.fontStyle = (v === 'residence') ? 'italic' : '';
  }
  orgInput.addEventListener('input', function() { checkOrgItalic(); updateBarCustomer(); });
  orgInput.addEventListener('change', function() { checkOrgItalic(); updateBarCustomer(); });
  checkOrgItalic();
  var nameInput = wrap.querySelector('[data-field="name"]');
  if (nameInput) {
    nameInput.addEventListener('input', updateBarCustomer);
    nameInput.addEventListener('change', updateBarCustomer);
  }

  /* ── Customer history index (shared by name + email + org autocomplete) ── */
  var customerEmailCache = null;
  API.get('/customer-emails').then(function(data) {
    customerEmailCache = data.emails || [];
  }).catch(function() { customerEmailCache = []; });

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Stashed historical
     ship-to / bill-to blocks from the autocomplete cache. Set on
     autoFillCustomerFromMatch; consumed when the rep ticks the matching
     Ship To / Bill To toggle (see bindAddrToggleStash below). Not applied
     automatically because alternate addresses are often order-specific
     (different job site / campus) — toggle-gated keeps the surface clean. */
  var _stashedShipTo = null;
  var _stashedBillTo = null;
  function _blockHasData(b) {
    if (!b) return false;
    var keys = Object.keys(b);
    for (var i = 0; i < keys.length; i++) {
      var v = b[keys[i]];
      if (v && v !== '—' && v !== '-') return true;
    }
    return false;
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function autoFillCustomerFromMatch(match) {
    if (!match) return;
    var fieldMap = { name: 'name', company: 'company', phone: 'phone', street: 'street', street2: 'street2', city: 'city', state: 'state', zip: 'zip', email: 'email', attn: 'attn' };
    Object.keys(fieldMap).forEach(function(key) {
      if (!match[key] || match[key] === '—' || match[key] === '-') return;
      var fname = fieldMap[key];
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — email field is now a chip
         field: seed a chip only when none present yet. */
      if (fname === 'email' && _emailChipApis.email) {
        if (_emailChipApis.email.getChips().length === 0) _emailChipApis.email.add(match[key]);
        return;
      }
      var field = wrap.querySelector('[data-field="' + fname + '"]');
      if (field && !field.value.trim()) field.value = match[key];
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — stash ship/bill blocks silently */
    _stashedShipTo = _blockHasData(match.shipTo) ? match.shipTo : null;
    _stashedBillTo = _blockHasData(match.billTo) ? match.billTo : null;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    updateBarCustomer();
  }

  /* ── Name autocomplete from customer history ── */
  (function() {
    var nameInput = wrap.querySelector('[data-field="name"]');
    if (!nameInput) return;
    var nameDropdown = document.createElement('div');
    nameDropdown.className = 'rq-ws-email-dropdown';
    var nameIdx = -1;
    nameInput.parentElement.style.position = 'relative';
    nameInput.parentElement.appendChild(nameDropdown);

    function nameFilter(query) {
      if (!customerEmailCache || !query || query.length < 2) return [];
      var q = query.toLowerCase();
      return customerEmailCache.filter(function(c) {
        return c.name && c.name.toLowerCase().indexOf(q) !== -1;
      }).slice(0, 6);
    }

    nameInput.addEventListener('input', function() {
      var val = this.value.trim();
      var matches = nameFilter(val);
      if (!matches.length) { nameDropdown.style.display = 'none'; nameIdx = -1; return; }
      nameDropdown.innerHTML = matches.map(function(m) {
        var label = m.name;
        var ctx = (m.company && m.company !== '—' && m.company !== '-') ? m.company : m.email || '';
        if (ctx) label += ' <span style="opacity:0.6;font-size:11px;">— ' + ctx + '</span>';
        return '<div class="rq-ws-email-dropdown__item" data-email="' + (m.email || '') + '">' + label + '</div>';
      }).join('');
      nameDropdown.style.display = 'block';
      nameIdx = -1;
    });

    function namePick(item) {
      var email = item.getAttribute('data-email');
      nameDropdown.style.display = 'none';
      nameIdx = -1;
      if (customerEmailCache && email) {
        var match = customerEmailCache.find(function(c) { return c.email === email; });
        if (match && match.name) nameInput.value = match.name;
        autoFillCustomerFromMatch(match);
      }
    }

    nameDropdown.addEventListener('mousedown', function(e) {
      e.preventDefault();
      var item = e.target.closest('.rq-ws-email-dropdown__item');
      if (item) namePick(item);
    });

    nameInput.addEventListener('blur', function() { setTimeout(function() { nameDropdown.style.display = 'none'; nameIdx = -1; }, 150); });
    nameInput.addEventListener('keydown', function(e) {
      if (nameDropdown.style.display !== 'block') return;
      var items = nameDropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nameIdx = nameIdx < items.length - 1 ? nameIdx + 1 : 0;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === nameIdx); });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nameIdx = nameIdx > 0 ? nameIdx - 1 : items.length - 1;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === nameIdx); });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (nameIdx >= 0) { e.preventDefault(); namePick(items[nameIdx]); }
        else if (items.length) { e.preventDefault(); namePick(items[0]); }
      } else if (e.key === 'Escape') {
        nameDropdown.style.display = 'none'; nameIdx = -1;
      }
    });
  })();

  /* ── Organization autocomplete from customer history ── */
  (function() {
    var orgDropdown = document.createElement('div');
    orgDropdown.className = 'rq-ws-email-dropdown';
    var orgIdx = -1;
    orgInput.parentElement.style.position = 'relative';
    orgInput.parentElement.appendChild(orgDropdown);

    function orgFilter(query) {
      if (!customerEmailCache || !query || query.length < 2) return [];
      var q = query.toLowerCase();
      var seen = {};
      return customerEmailCache.filter(function(c) {
        if (!c.company || seen[c.company.toLowerCase()]) return false;
        if (c.company.toLowerCase().indexOf(q) === -1) return false;
        seen[c.company.toLowerCase()] = true;
        return true;
      }).slice(0, 6);
    }

    orgInput.addEventListener('input', function() {
      checkOrgItalic();
      var val = this.value.trim();
      var matches = orgFilter(val);
      if (!matches.length) { orgDropdown.style.display = 'none'; orgIdx = -1; return; }
      orgDropdown.innerHTML = matches.map(function(m) {
        return '<div class="rq-ws-email-dropdown__item" data-company="' + m.company.replace(/"/g, '&quot;') + '">' + m.company + '</div>';
      }).join('');
      orgDropdown.style.display = 'block';
      orgIdx = -1;
    });

    function orgPick(item) {
      var company = item.getAttribute('data-company');
      orgInput.value = company;
      orgDropdown.style.display = 'none';
      orgIdx = -1;
      checkOrgItalic();
      if (customerEmailCache) {
        var match = customerEmailCache.find(function(c) { return c.company && c.company.toLowerCase() === company.toLowerCase(); });
        autoFillCustomerFromMatch(match);
      }
    }

    orgDropdown.addEventListener('mousedown', function(e) {
      e.preventDefault();
      var item = e.target.closest('.rq-ws-email-dropdown__item');
      if (item) orgPick(item);
    });

    orgInput.addEventListener('blur', function() {
      setTimeout(function() { orgDropdown.style.display = 'none'; orgIdx = -1; }, 150);
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-03) — duplicate-lead nudge (must run
         BEFORE the CFA early-returns below, so it fires even when CFA is already set). */
      _maybeNudgeDuplicateLead();
      // CFA lookup by company (fallback if email didn't match)
      var company = orgInput.value.trim();
      if (!company || company.toLowerCase() === 'residence') return;
      var sel = wrap.querySelector('select[data-field="cfaAttribution"]');
      if (!sel || (state.cfaAttribution && state.cfaAttribution !== 'none' && state.cfaAttribution !== '')) return;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — never on a replacement. The guard
         above treats 'none' as "not chosen yet", so without this a blur here would stamp
         the customer's CFA onto a $0 re-ship — and a save carrying a CFA code mints a
         10% attribution record when the customer has none. See applyReplacementMode. */
      if (state.isReplacement) return;
      API.get('/cfa-attribution?company=' + encodeURIComponent(company)).then(function(data) {
        if (data.attribution && data.attribution.cfa) {
          state.cfaAttribution = data.attribution.cfa;
          sel.value = data.attribution.cfa;
          sel.style.outline = '2px solid #22c55e';
          setTimeout(function() { sel.style.outline = ''; }, 2000);
        }
      }).catch(function() {});
    });

    orgInput.addEventListener('keydown', function(e) {
      if (orgDropdown.style.display !== 'block') return;
      var items = orgDropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        orgIdx = orgIdx < items.length - 1 ? orgIdx + 1 : 0;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === orgIdx); });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        orgIdx = orgIdx > 0 ? orgIdx - 1 : items.length - 1;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === orgIdx); });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (orgIdx >= 0) { e.preventDefault(); orgPick(items[orgIdx]); }
        else if (items.length) { e.preventDefault(); orgPick(items[0]); }
      } else if (e.key === 'Escape') {
        orgDropdown.style.display = 'none'; orgIdx = -1;
      }
    });
  })();

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-03) — Duplicate-lead prevention nudge.
     When the rep enters a customer whose email/company already has an OPEN lead (a
     separate inquiry), surface a non-blocking banner steering them to build from that
     lead so its form submission + ad/marketing attribution carry over — the mistake
     that stranded RW-1225-A off its RW-1216-A ad lead. /lead-lookup returns ONLY open
     leads, so a properly-built quote (its own lead is 'quoted') never self-triggers.
     Dismissible; never blocks the save. Fired from the org-blur above. */
  var _dupLeadChecked = '';
  var _dupLeadDismissed = {};
  function _removeDuplicateLeadBanner() {
    if (Q.hideAlert) Q.hideAlert('dup-lead'); // CUSTOM_QWALERTS_BY_CLAUDE (Pass 2)
    var ex = document.getElementById('rq-ws-duplead-nudge');
    if (ex && ex.parentNode) ex.parentNode.removeChild(ex);
  }
  function _maybeNudgeDuplicateLead() {
    try {
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — the nudge's job is to steer a NEW
      // quote to build from an existing open lead. On an already-saved/edited quote
      // (mode 'edit') that ship has sailed, so it's just noise (e.g. RW-1239-A, already
      // quoted, flagging Aaron's separate open lead). Only fire while building fresh.
      if (state.mode === 'edit') return;
      var emailEl = wrap.querySelector('[data-field="email"]');
      var orgEl = wrap.querySelector('[data-field="company"]');
      var email = ((emailEl && emailEl.value) || '').trim();
      var company = ((orgEl && orgEl.value) || '').trim();
      if (company && company.toLowerCase() === 'residence') company = '';
      if (!email && !company) return;
      var key = (email + '|' + company).toLowerCase();
      if (key === _dupLeadChecked || _dupLeadDismissed[key]) return; // don't re-spam the same customer
      _dupLeadChecked = key;
      var qs = [];
      if (email) qs.push('email=' + encodeURIComponent(email));
      if (company) qs.push('company=' + encodeURIComponent(company));
      if (!qs.length) return;
      API.get('/lead-lookup?' + qs.join('&')).then(function(data) {
        var leads = (data && Array.isArray(data.leads)) ? data.leads.filter(function(l) { return l && l.ref !== state.ref; }) : [];
        if (leads.length) _showDuplicateLeadBanner(leads[0], key);
        else _removeDuplicateLeadBanner();
      }).catch(function() {});
    } catch (e) { /* non-fatal */ }
  }
  function _showDuplicateLeadBanner(lead, key) {
    _removeDuplicateLeadBanner();
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — label by the person's name when the
    // org is a residence placeholder, else the banner reads "N/a - Residence already
    // has an open lead" instead of the actual customer (e.g. "Aaron Connacher").
    var _co = String(lead.company || '').trim();
    var _coReal = _co && !/\b(residence|residential|home)\b/i.test(_co) && !/^n\/?a\b/i.test(_co) && _co.toLowerCase() !== 'none';
    var name = (_coReal ? _co : (lead.customerName || _co)) || 'This customer';
    /* CUSTOM_QWALERTS_BY_CLAUDE (Ryan 2026-10-23, Pass 2) - one row in the alerts banner instead of a box above the Back
       link. "Build from lead" is its button; the x is "Keep this quote" (not asked again for this customer). */
    if (Q.showAlert) {
      var _buildLabel = 'Build from lead ' + lead.ref;
      Q.showAlert({ id: 'dup-lead', level: 'yellow', icon: '&#x1F465;', persist: false,
        head: name + ' already has an open lead',
        text: lead.ref + (lead.formName ? ' (' + lead.formName + ')' : '') + '. Build the quote from it so the form submission and ad attribution carry over.',
        onDismiss: function () { _dupLeadDismissed[key] = true; },
        action: { label: _buildLabel, run: function (btn) {
          btn.disabled = true; btn.textContent = 'Opening...';
          API.post('/create-quote-from-lead', { ref: lead.ref }).then(function (res) {
            window.location.href = (res && res.redirectUrl) ? res.redirectUrl : ('/pages/rep-quote-workstation?ref=' + encodeURIComponent(lead.ref));
          }).catch(function (err) {
            btn.disabled = false; btn.textContent = _buildLabel;
            alert('Could not open the lead: ' + ((err && err.message) || 'error'));
          });
        } } });
      return;
    }
    var el = document.createElement('div');
    el.id = 'rq-ws-duplead-nudge';
    el.style.cssText = 'margin:0 0 12px;padding:10px 12px;border:1px solid #f59e0b;background:#fffbeb;color:#92400e;border-radius:8px;font-size:13px;display:flex;align-items:flex-start;gap:10px;';
    el.innerHTML =
      '<span style="font-size:16px;line-height:1;">&#9888;</span>' +
      '<div style="flex:1;">' +
        '<strong>' + escHtml(name) + '</strong> already has an open lead <strong>' + escHtml(lead.ref) + '</strong>' +
        (lead.formName ? ' (' + escHtml(lead.formName) + ')' : '') +
        '. Build the quote from it so the form submission and ad attribution carry over.' +
        '<div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">' +
          '<button type="button" id="rq-ws-duplead-build" style="cursor:pointer;border:0;border-radius:6px;padding:5px 10px;background:#d97706;color:#fff;font-size:12px;">Build from lead ' + escHtml(lead.ref) + ' &rarr;</button>' +
          '<button type="button" id="rq-ws-duplead-dismiss" style="cursor:pointer;border:0;border-radius:6px;padding:5px 10px;background:transparent;color:#92400e;font-size:12px;text-decoration:underline;">Keep this quote</button>' +
        '</div>' +
      '</div>';
    wrap.insertBefore(el, wrap.firstChild);
    var buildBtn = el.querySelector('#rq-ws-duplead-build');
    if (buildBtn) buildBtn.addEventListener('click', function() {
      buildBtn.disabled = true; buildBtn.textContent = 'Opening…';
      API.post('/create-quote-from-lead', { ref: lead.ref }).then(function(res) {
        window.location.href = (res && res.redirectUrl) ? res.redirectUrl : ('/pages/rep-quote-workstation?ref=' + encodeURIComponent(lead.ref));
      }).catch(function(err) {
        buildBtn.disabled = false; buildBtn.textContent = 'Build from lead ' + lead.ref + ' →';
        alert('Could not open the lead: ' + ((err && err.message) || 'error'));
      });
    });
    var dismissBtn = el.querySelector('#rq-ws-duplead-dismiss');
    if (dismissBtn) dismissBtn.addEventListener('click', function() { _dupLeadDismissed[key] = true; _removeDuplicateLeadBanner(); });
  }

  /* ── Email domain autocomplete ── */
  var emailDomains = ['gmail.com','yahoo.com','outlook.com','hotmail.com','aol.com','icloud.com','me.com','msn.com','live.com','comcast.net','att.net','sbcglobal.net','verizon.net','cox.net','charter.net'];
  function bindEmailAutocomplete(input, chipApi) {
    if (!input) return;
    var dropdown = document.createElement('div');
    dropdown.className = 'rq-ws-email-dropdown';
    var activeIdx = -1;
    var skipNextInput = false;
    input.parentElement.style.position = 'relative';
    input.parentElement.appendChild(dropdown);

    input.addEventListener('input', function() {
      if (skipNextInput) { skipNextInput = false; return; }
      var val = this.value;
      var atIdx = val.lastIndexOf('@');
      if (atIdx < 1) { dropdown.style.display = 'none'; return; }
      var partial = val.substring(atIdx + 1).toLowerCase();
      var prefix = val.substring(0, atIdx);
      var matches = partial ? emailDomains.filter(function(d) { return d.indexOf(partial) === 0 && partial !== d; }) : emailDomains;
      if (!matches.length) { dropdown.style.display = 'none'; activeIdx = -1; return; }
      dropdown.innerHTML = matches.slice(0, 5).map(function(d) {
        return '<div class="rq-ws-email-dropdown__item">' + prefix + '@' + d + '</div>';
      }).join('');
      dropdown.style.display = 'block';
      activeIdx = -1;
    });

    function pick(item) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — chip-aware: add the
         completed address as a chip instead of filling the input. */
      if (chipApi) { chipApi.add(item.textContent); input.value = ''; }
      else { skipNextInput = true; input.value = item.textContent; }
      dropdown.style.display = 'none';
      activeIdx = -1;
    }

    dropdown.addEventListener('mousedown', function(e) {
      e.preventDefault();
      var item = e.target.closest('.rq-ws-email-dropdown__item');
      if (item) pick(item);
    });

    function highlightItem(idx) {
      var items = dropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === idx); });
      activeIdx = idx;
    }

    input.addEventListener('blur', function() { setTimeout(function() { dropdown.style.display = 'none'; activeIdx = -1; }, 150); });
    input.addEventListener('keydown', function(e) {
      if (dropdown.style.display !== 'block') return;
      var items = dropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlightItem(activeIdx < items.length - 1 ? activeIdx + 1 : 0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlightItem(activeIdx > 0 ? activeIdx - 1 : items.length - 1);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(activeIdx >= 0 ? items[activeIdx] : items[0]);
      } else if (e.key === 'Escape') {
        dropdown.style.display = 'none';
        activeIdx = -1;
      }
    });
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — bind ship/bill domain
     autocomplete to their chip inputs (the [data-field] elements are now hidden
     mirrors); pick adds a chip. */
  ['shipEmail', 'billEmail'].forEach(function(f) {
    if (_emailChipApis[f]) bindEmailAutocomplete(_emailChipApis[f].input, _emailChipApis[f]);
    else { var el = wrap.querySelector('[data-field="' + f + '"]'); if (el) bindEmailAutocomplete(el); }
  });

  /* ── Historical customer email autocomplete (primary email field) ── */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — bind the historical-email
     autocomplete to the chip field's visible typing input (the [data-field=
     "email"] element is now a hidden mirror). */
  var primaryEmailInput = (_emailChipApis.email && _emailChipApis.email.input) || wrap.querySelector('[data-field="email"]');
  if (primaryEmailInput) {
    var ceDropdown = document.createElement('div');
    ceDropdown.className = 'rq-ws-email-dropdown';
    var ceActiveIdx = -1;
    primaryEmailInput.parentElement.style.position = 'relative';
    primaryEmailInput.parentElement.appendChild(ceDropdown);

    function ceFilter(query) {
      if (!customerEmailCache || !query || query.length < 2) return [];
      var q = query.toLowerCase();
      return customerEmailCache.filter(function(c) {
        return c.email.indexOf(q) !== -1 || (c.name && c.name.toLowerCase().indexOf(q) !== -1) || (c.company && c.company.toLowerCase().indexOf(q) !== -1);
      }).slice(0, 6);
    }

    function ceRender(matches) {
      if (!matches.length) { ceDropdown.style.display = 'none'; ceActiveIdx = -1; return; }
      ceDropdown.innerHTML = matches.map(function(m) {
        var label = m.email;
        var ctx = (m.company && m.company !== '—' && m.company !== '-') ? m.company : (m.name && m.name !== '—' && m.name !== '-') ? m.name : '';
        if (ctx) label += ' <span style="opacity:0.6;font-size:11px;">— ' + ctx + '</span>';
        return '<div class="rq-ws-email-dropdown__item" data-email="' + m.email + '">' + label + '</div>';
      }).join('');
      ceDropdown.style.display = 'block';
      ceActiveIdx = -1;
    }

    primaryEmailInput.addEventListener('input', function() {
      var val = this.value.trim();
      // If typing has an @ but no domain match yet, fall back to domain autocomplete
      var atIdx = val.lastIndexOf('@');
      if (atIdx > 0 && val.substring(atIdx + 1).length > 0) {
        // Check if any historical emails match first
        var hMatches = ceFilter(val);
        if (hMatches.length) { ceRender(hMatches); return; }
        // Fall back to domain autocomplete
        var partial = val.substring(atIdx + 1).toLowerCase();
        var prefix = val.substring(0, atIdx);
        var dMatches = partial ? emailDomains.filter(function(d) { return d.indexOf(partial) === 0 && partial !== d; }) : emailDomains;
        if (dMatches.length) {
          ceDropdown.innerHTML = dMatches.slice(0, 5).map(function(d) {
            return '<div class="rq-ws-email-dropdown__item" data-email="' + prefix + '@' + d + '">' + prefix + '@' + d + '</div>';
          }).join('');
          ceDropdown.style.display = 'block';
          ceActiveIdx = -1;
          return;
        }
        ceDropdown.style.display = 'none';
        return;
      }
      ceRender(ceFilter(val));
    });


    function cePick(item) {
      var email = item.getAttribute('data-email');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — add the picked address as a
         chip (was: set the input value). */
      if (_emailChipApis.email) { _emailChipApis.email.add(email); primaryEmailInput.value = ''; }
      else { primaryEmailInput.value = email; }
      ceDropdown.style.display = 'none';
      ceActiveIdx = -1;
      if (customerEmailCache) {
        var match = customerEmailCache.find(function(c) { return c.email === email; });
        autoFillCustomerFromMatch(match);
      }
    }

    ceDropdown.addEventListener('mousedown', function(e) {
      e.preventDefault();
      var item = e.target.closest('.rq-ws-email-dropdown__item');
      if (item) cePick(item);
    });

    primaryEmailInput.addEventListener('blur', function() { setTimeout(function() { ceDropdown.style.display = 'none'; ceActiveIdx = -1; }, 150); });
    primaryEmailInput.addEventListener('keydown', function(e) {
      if (ceDropdown.style.display !== 'block') return;
      var items = ceDropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        ceActiveIdx = ceActiveIdx < items.length - 1 ? ceActiveIdx + 1 : 0;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === ceActiveIdx); });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        ceActiveIdx = ceActiveIdx > 0 ? ceActiveIdx - 1 : items.length - 1;
        items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === ceActiveIdx); });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (ceActiveIdx >= 0) { e.preventDefault(); cePick(items[ceActiveIdx]); }
        else if (items.length) { e.preventDefault(); cePick(items[0]); }
      } else if (e.key === 'Escape') {
        ceDropdown.style.display = 'none'; ceActiveIdx = -1;
      }
    });
  }

  /* ── CFA auto-detection on customer email ── */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — CFA auto-detect off the chip
     field's typing input, keyed on the PRIMARY (first) email. */
  var cfaEmailField = (_emailChipApis.email && _emailChipApis.email.input) || wrap.querySelector('[data-field="email"]');
  var cfaLastChecked = '';
  if (cfaEmailField) {
    cfaEmailField.addEventListener('blur', function() {
      var email = (_emailChipApis.email ? _emailChipApis.email.primary() : cfaEmailField.value).trim().toLowerCase();
      if (!email || email === cfaLastChecked || !email.includes('@')) return;
      cfaLastChecked = email;
      var sel = wrap.querySelector('select[data-field="cfaAttribution"]');
      if (!sel) return;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — never on a replacement, same as the
         company-blur twin above: the "only auto-fill if the user hasn't chosen" test below
         reads 'none' as unchosen, so this path would silently attribute a $0 re-ship. */
      if (state.isReplacement) return;
      var cfaUrl = '/cfa-attribution?email=' + encodeURIComponent(email);
      var company = (wrap.querySelector('[data-field="company"]') || {}).value;
      if (company && company.trim()) cfaUrl += '&company=' + encodeURIComponent(company.trim());
      API.get(cfaUrl).then(function(data) {
        if (data.attribution && data.attribution.cfa) {
          // Only auto-fill if user hasn't already chosen a CFA
          if (state.cfaAttribution === 'none' || state.cfaAttribution === '') {
            state.cfaAttribution = data.attribution.cfa;
            sel.value = data.attribution.cfa;
            sel.style.outline = '2px solid #22c55e';
            setTimeout(function() { sel.style.outline = ''; }, 2000);
          }
        }
      }).catch(function() {});
    });
  }

  /* ── State abbreviation autocomplete ── */
  var usStates = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
    ['DC','District of Columbia']
  ];
  function bindStateAutocomplete(input) {
    var dropdown = document.createElement('div');
    dropdown.className = 'rq-ws-email-dropdown';
    var activeIdx = -1;
    var skipNextInput = false;
    input.parentElement.style.position = 'relative';
    input.parentElement.appendChild(dropdown);

    input.addEventListener('input', function() {
      if (skipNextInput) { skipNextInput = false; return; }
      var val = this.value.toUpperCase();
      if (!val) { dropdown.style.display = 'none'; activeIdx = -1; return; }
      var matches = usStates.filter(function(s) {
        return s[0].indexOf(val) === 0 || s[1].toUpperCase().indexOf(val) === 0;
      });
      if (!matches.length || (matches.length === 1 && matches[0][0] === val)) {
        dropdown.style.display = 'none'; activeIdx = -1; return;
      }
      dropdown.innerHTML = matches.slice(0, 8).map(function(s) {
        return '<div class="rq-ws-email-dropdown__item" data-abbr="' + s[0] + '"><strong>' + s[0] + '</strong> &nbsp;' + s[1] + '</div>';
      }).join('');
      dropdown.style.display = 'block';
      activeIdx = -1;
    });

    function pick(item) {
      skipNextInput = true;
      input.value = item.getAttribute('data-abbr');
      dropdown.style.display = 'none';
      activeIdx = -1;
    }

    dropdown.addEventListener('mousedown', function(e) {
      e.preventDefault();
      var item = e.target.closest('.rq-ws-email-dropdown__item');
      if (item) pick(item);
    });

    function highlightItem(idx) {
      var items = dropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      items.forEach(function(el, i) { el.classList.toggle('rq-ws-email-dropdown__item--active', i === idx); });
      activeIdx = idx;
    }

    input.addEventListener('blur', function() { setTimeout(function() { dropdown.style.display = 'none'; activeIdx = -1; }, 150); });
    input.addEventListener('keydown', function(e) {
      if (dropdown.style.display !== 'block') return;
      var items = dropdown.querySelectorAll('.rq-ws-email-dropdown__item');
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlightItem(activeIdx < items.length - 1 ? activeIdx + 1 : 0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlightItem(activeIdx > 0 ? activeIdx - 1 : items.length - 1);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(activeIdx >= 0 ? items[activeIdx] : items[0]);
      } else if (e.key === 'Escape') {
        dropdown.style.display = 'none';
        activeIdx = -1;
      }
    });
  }
  wrap.querySelectorAll('[data-field="state"], [data-field="shipState"], [data-field="billState"]').forEach(bindStateAutocomplete);

  /* ── Ship To / Bill To address toggles ── */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Apply stashed history
     to the relevant block when the rep ticks the toggle. Fills only empty
     fields (never clobbers what the rep already typed). Surfaces a small
     "Pre-filled from last quote" hint that auto-clears on first edit or
     after 8s. */
  function _applyStashedAddress(addrKey, stash) {
    if (!stash) return false;
    var prefix = addrKey === 'shipTo' ? 'ship' : 'bill';
    var keyMap = { name: 'Name', email: 'Email', phone: 'Phone', company: 'Company', street: 'Street', street2: 'Street2', city: 'City', state: 'State', zip: 'Zip', attn: 'Attn' };
    var anyFilled = false;
    Object.keys(keyMap).forEach(function(k) {
      var v = stash[k];
      if (!v || v === '—' || v === '-') return;
      var field = wrap.querySelector('[data-field="' + prefix + keyMap[k] + '"]');
      if (field && !field.value.trim()) {
        field.value = v;
        anyFilled = true;
      }
    });
    return anyFilled;
  }
  function _showStashHint(addrKey) {
    var section = wrap.querySelector('[data-addr-section="' + addrKey + '"]');
    if (!section) return;
    var existing = section.querySelector('.rq-ws-addr-stash-hint');
    if (existing) return;
    var hint = document.createElement('div');
    hint.className = 'rq-ws-addr-stash-hint';
    hint.textContent = 'Pre-filled from last quote — edit as needed';
    hint.style.cssText = 'font-size:11px;font-style:italic;color:var(--rq-text-secondary,#6b7280);padding:6px 0 4px;';
    section.insertBefore(hint, section.firstChild);
    var clear = function() { if (hint && hint.parentNode) hint.parentNode.removeChild(hint); };
    var prefix = addrKey === 'shipTo' ? 'ship' : 'bill';
    section.querySelectorAll('[data-field^="' + prefix + '"]').forEach(function(f) {
      f.addEventListener('input', clear, { once: true });
    });
    setTimeout(clear, 8000);
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  wrap.querySelectorAll('[data-addr-toggle]').forEach(function(cb) {
    var key = cb.getAttribute('data-addr-toggle');
    var section = wrap.querySelector('[data-addr-section="' + key + '"]');
    if (!section) return;
    cb.addEventListener('change', function() {
      if (this.checked) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — apply stashed history before measuring
           scrollHeight so the transition expands to the populated size. */
        var stash = key === 'shipTo' ? _stashedShipTo : (key === 'billTo' ? _stashedBillTo : null);
        if (_applyStashedAddress(key, stash)) _showStashHint(key);
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        section.classList.add('rq-ws-addr-section--open');
        section.style.maxHeight = section.scrollHeight + 'px';
        var s = section;
        var onEnd = function() {
          s.style.maxHeight = 'none';
          s.removeEventListener('transitionend', onEnd);
        };
        section.addEventListener('transitionend', onEnd);
      } else {
        section.style.maxHeight = section.scrollHeight + 'px';
        section.offsetHeight;
        section.style.maxHeight = '0';
        section.classList.remove('rq-ws-addr-section--open');
      }
    });
  });

  function getCustomerData() {
    var customer = {};
    ['name', 'email', 'phone', 'company',
     'street', 'street2', 'city', 'state', 'zip', 'attn', 'notes',
     'shipName', 'shipEmail', 'shipPhone', 'shipCompany',
     'shipStreet', 'shipStreet2', 'shipCity', 'shipState', 'shipZip', 'shipAttn',
     'billName', 'billEmail', 'billPhone', 'billCompany',
     'billStreet', 'billStreet2', 'billCity', 'billState', 'billZip', 'billAttn'].forEach(function(f) {
      var el = wrap.querySelector('[data-field="' + f + '"]');
      customer[f] = el ? el.value.trim() : '';
    });
    /* Leave blank fields empty — PDF handles dash fallback for display */
    /* Ship To / Bill To flags */
    var shipCb = wrap.querySelector('[data-addr-toggle="shipTo"]');
    var billCb = wrap.querySelector('[data-addr-toggle="billTo"]');
    customer.hasShipTo = shipCb ? shipCb.checked : false;
    customer.hasBillTo = billCb ? billCb.checked : false;
    /* CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) - the To / CC people. `email` stays every address, the main contact
       first (what Shopify, the PDF and thread matching read); `contacts` is everyone after the main contact with their
       role, name and the phone / title the record had. Always sent: this save replaces the stored customer whole, so a
       save that left them out would strip what the DD added. */
    var toApi = _emailChipApis.email, ccApi = _emailChipApis.emailCc;
    if (toApi && ccApi && toApi.getPeople && ccApi.getPeople) {
      var toP = toApi.getPeople(), ccP = ccApi.getPeople();
      var withInfo = function(p, role) { var x = _peopleExtras[String(p.email).toLowerCase()] || {}; return { name: p.name || '', email: p.email, phone: x.phone || '', title: x.title || '', role: role }; };
      customer.email = toP.concat(ccP).map(function(p) { return p.email; }).join(', ');
      customer.contacts = toP.slice(1).map(function(p) { return withInfo(p, 'to'); })
        .concat(ccP.map(function(p) { return withInfo(p, 'cc'); }))
        .concat(_peopleNoEmail.map(function(c) { return { name: c.name || '', title: c.title || '', phone: c.phone || '', email: '', role: c.role === 'to' ? 'to' : 'cc' }; }));
    }
    return customer;
  }

  function updateBarCustomer() {
    var compEl = wrap.querySelector('[data-field="company"]');
    var nameEl = wrap.querySelector('[data-field="name"]');
    var org = compEl ? compEl.value.trim() : '';
    var name = nameEl ? nameEl.value.trim() : '';
    var label = org && name ? org + ' - ' + name : org || name;
    if (label && state.ref) label += '  -  ' + state.ref;
    else if (state.ref) label = state.ref;
    dom.barCustomer.textContent = label;
    dom.barRef.style.display = label && state.ref ? 'none' : '';
  }

  /* ── Exports ── */
  Q.populateCustomer = populateCustomer;
  Q.resetToNew = resetToNew;
  Q.setStatusBadge = setStatusBadge;
  Q.updateBarCustomer = updateBarCustomer;
  Q.getCustomerData = getCustomerData;
})();
